(() => {
  if (window.CedarAmberProductPage) {
    window.CedarAmberProductPage.init();
    return;
  }

  const instances = new Map();

  function mount(root) {
    if (instances.has(root)) return;
    const life = new AbortController();
    const signal = life.signal;
    const $ = (selector, context = root) => context.querySelector(selector);
    const $$ = (selector, context = root) => [...context.querySelectorAll(selector)];
    let strings = {};
    try { strings = JSON.parse($('[data-ca-pdp-strings]')?.textContent || '{}'); } catch {}
    let variantRequest;
    let variantRevision = 0;
    let adding = false;
    let updating = false;
    let scrollFrame = 0;
    let galleryRevision = 0;
    let storyRevision = 0;
    let lightboxOpener;
    let lockedOverflow;
    let touchStart;
    let suppressClickUntil = 0;
    const modelUIs = new Map();
    const gallery = $('[data-ca-gallery]');
    const media = $$('[data-ca-media]');
    const thumbs = $$('[data-ca-thumbnail]');
    const photos = media.filter(frame => frame.dataset.mediaType === 'image' && $('[data-ca-zoom]', frame));
    let galleryIndex = Math.max(0, media.findIndex(frame => frame.classList.contains('is-active')));
    let galleryIntentIndex = galleryIndex;
    let galleryRequest;
    let galleryStatus;
    const gallerySources = new Map(media.map(frame => {
      const image = $('img', frame);
      const anchor = $('[data-ca-zoom]', frame);
      return [frame, image ? { image, src: image.getAttribute('src'), srcset: image.getAttribute('srcset'), sizes: image.getAttribute('sizes'), alt: image.alt, href: anchor?.getAttribute('href') } : null];
    }));
    let lightboxIndex = 0;
    let lightboxRevision = 0;
    let lightboxRequest;
    let committedOptions = [];
    const lightbox = $('[data-ca-lightbox]');
    const sticky = $('[data-ca-sticky]');
    let purchaseFeedback;
    let purchaseAnnouncement;
    let stickyStatus;
    let activeStatusRegion;

    const on = (target, event, handler, options = {}) => target?.addEventListener(event, handler, { ...options, signal });
    const purchase = () => $('[data-ca-purchase]');
    const productForm = () => $('.ca-pdp-product-form');
    const status = () => $('[data-ca-form-status]');
    const available = () => purchase()?.dataset.available === 'true' && !!purchase()?.dataset.variantId;

    function renderPurchaseStatus(force = false) {
      if (!purchaseFeedback || signal.aborted || !root.isConnected) return;
      const heroStatus = status();
      const region = sticky && !sticky.hidden ? stickyStatus : heroStatus;
      if (!force && activeStatusRegion === region) return;
      const focusedLink = activeStatusRegion?.querySelector('a') === document.activeElement;
      const focusedMessage = activeStatusRegion?.querySelector('span') === document.activeElement;
      activeStatusRegion = region;
      [heroStatus, stickyStatus].filter(Boolean).forEach(node => {
        node.removeAttribute('role');
        node.setAttribute('aria-live', 'off');
        node.textContent = '';
        node.classList.toggle('is-error', purchaseFeedback.isError);
      });
      const visible = !!purchaseFeedback.message;
      if (stickyStatus) stickyStatus.hidden = !visible || region !== stickyStatus;
      sticky?.classList.toggle('has-purchase-status', visible && region === stickyStatus);
      if (!region || !visible) return;
      const message = document.createElement('span');
      message.textContent = purchaseFeedback.message;
      if (purchaseFeedback.isError) message.tabIndex = 0;
      region.append(message);
      if (focusedMessage && purchaseFeedback.isError) message.focus({ preventScroll: true });
      if (purchaseFeedback.withCart) {
        const link = document.createElement('a');
        link.href = root.dataset.cartUrl;
        link.textContent = strings.cartLink || 'View your bag';
        region.append(' ', link);
        if (focusedLink) link.focus({ preventScroll: true });
      }
    }

    function setStatus(message, isError = false, withCart = false) {
      if (signal.aborted || !root.isConnected) return;
      purchaseFeedback = { message: message || '', isError, withCart };
      if (!purchaseAnnouncement) {
        purchaseAnnouncement = document.createElement('p');
        purchaseAnnouncement.className = 'ca-pdp-sr';
        purchaseAnnouncement.dataset.caPurchaseAnnouncement = '';
        purchaseAnnouncement.setAttribute('role', 'status');
        purchaseAnnouncement.setAttribute('aria-live', 'polite');
        purchaseAnnouncement.setAttribute('aria-atomic', 'true');
        root.append(purchaseAnnouncement);
      }
      if (sticky && !stickyStatus) {
        stickyStatus = document.createElement('p');
        stickyStatus.className = 'ca-pdp-sticky-status';
        stickyStatus.dataset.caStickyStatus = '';
        stickyStatus.hidden = true;
        sticky.prepend(stickyStatus);
      }
      renderPurchaseStatus(true);
      purchaseAnnouncement.textContent = purchaseFeedback.message;
    }

    function syncNativeWallets() {
      const wrapper = $('[data-ca-native-wallets]');
      if (!wrapper) return;
      const quantity = $('[data-ca-quantity-input]');
      const invalidQuantity = quantity && (!quantity.value || !quantity.checkValidity());
      const blocked = adding || updating || !available() || !!invalidQuantity;
      wrapper.inert = blocked;
      wrapper.setAttribute('aria-disabled', String(blocked));
      wrapper.setAttribute('aria-busy', String(adding || updating));
    }

    function syncButtons() {
      const busy = adding || updating;
      const label = adding ? strings.adding : updating ? strings.updating : available() ? strings.add : purchase()?.dataset.variantId ? strings.soldOut : strings.unavailable;
      $$('[data-ca-add], [data-ca-sticky-add]').forEach(button => {
        button.disabled = busy || !available();
        button.setAttribute('aria-busy', String(busy));
        const text = button.querySelector('[data-ca-add-label], [data-ca-sticky-label]');
        if (text) text.textContent = label || 'Add to bag';
      });
      syncNativeWallets();
      const price = $('[data-ca-price]');
      const stickyPrice = $('[data-ca-sticky-price]');
      if (price && stickyPrice) {
        stickyPrice.textContent = price.textContent;
        const reference = $('[data-ca-reference-price]');
        const referenceLabel = $('[data-ca-reference-label]');
        const currentLabel = $('[data-ca-price-label]');
        const savings = $('[data-ca-savings]');
        let stickyReference = $('[data-ca-sticky-reference]');
        sticky?.classList.toggle('has-launch-pricing', !!reference);
        if (reference && referenceLabel && currentLabel) {
          const label = document.createElement('span');
          label.className = 'ca-pdp-sr';
          label.textContent = currentLabel.textContent.trim();
          const value = document.createElement('span');
          value.className = 'ca-pdp-sticky-price-value';
          value.textContent = price.textContent.trim();
          stickyPrice.replaceChildren(label, document.createTextNode(' '), value);
          if (!stickyReference) {
            stickyReference = document.createElement('small');
            stickyReference.className = 'ca-pdp-sticky-reference';
            stickyReference.dataset.caStickyReference = '';
            stickyPrice.after(stickyReference);
          }
          const regularLabel = document.createElement('span');
          regularLabel.className = 'ca-pdp-sr';
          regularLabel.textContent = referenceLabel.textContent.trim();
          const regularValue = document.createElement('s');
          regularValue.textContent = reference.textContent.trim();
          stickyReference.replaceChildren(regularLabel, document.createTextNode(' '), regularValue);
          if (savings) {
            const badge = document.createElement('span');
            badge.className = 'ca-pdp-savings';
            badge.textContent = savings.textContent.trim();
            stickyReference.append(document.createTextNode(' '), badge);
          }
        } else {
          stickyReference?.remove();
        }
      }
    }

    function syncQuantity() {
      const input = $('[data-ca-quantity-input]');
      if (!input) return;
      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      const quantity = Number(input.value);
      const less = $('[data-ca-quantity="-1"]');
      const more = $('[data-ca-quantity="1"]');
      if (less) less.disabled = adding || updating || !Number.isFinite(quantity) || quantity <= min;
      if (more) more.disabled = adding || updating || !Number.isFinite(quantity) || quantity >= max;
      syncNativeWallets();
    }

    function enhancePurchase() {
      const nativeSelect = $('[data-ca-native-id]');
      const nativeWrap = $('[data-ca-native-variants]');
      const variantId = $('[data-ca-variant-id]');
      const options = $('[data-ca-options]');
      if (nativeSelect && variantId) {
        nativeSelect.disabled = true;
        nativeWrap.hidden = true;
        variantId.disabled = !variantId.value;
      }
      if (options) options.hidden = false;
      $$('[data-ca-quantity]').forEach(button => { button.hidden = false; });
      committedOptions = $$('[data-ca-option-select]').map(select => select.value);
      syncButtons();
      syncQuantity();
    }

    function pauseFrame(frame) {
      frame?.querySelectorAll('video').forEach(video => video.pause());
      frame?.querySelectorAll('iframe').forEach(iframe => {
        try {
          const target = new URL(iframe.src).origin;
          iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), target);
          iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), target);
        } catch {}
      });
      modelUIs.get(frame)?.pause?.();
    }

    function activateModel(frame) {
      if (frame?.dataset.mediaType !== 'model' || modelUIs.has(frame)) return;
      const element = frame.querySelector('model-viewer');
      if (!element || !window.Shopify?.loadFeatures) return;
      window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0' }], error => {
        if (!error && root.isConnected && window.Shopify.ModelViewerUI && !modelUIs.has(frame)) {
          modelUIs.set(frame, new window.Shopify.ModelViewerUI(element));
        }
      });
    }

    function setGalleryStatus(message = '', failedIndex) {
      const stage = $('[data-ca-gallery-stage]');
      stage?.setAttribute('aria-busy', String(!!message && failedIndex === undefined));
      if (!galleryStatus && message && gallery) {
        galleryStatus = document.createElement('p');
        galleryStatus.className = 'ca-pdp-form-status';
        galleryStatus.dataset.caGalleryStatus = '';
        galleryStatus.setAttribute('role', 'status');
        galleryStatus.setAttribute('aria-live', 'polite');
        stage?.after(galleryStatus);
      }
      if (!galleryStatus) return;
      galleryStatus.textContent = message;
      galleryStatus.classList.toggle('is-error', failedIndex !== undefined);
      if (failedIndex !== undefined) {
        const retry = document.createElement('a');
        retry.href = gallerySources.get(media[failedIndex])?.href || thumbs[failedIndex]?.href || '#';
        retry.dataset.caGalleryRetry = String(failedIndex);
        retry.textContent = strings.galleryRetry || 'Try this photograph again';
        galleryStatus.append(' ', retry);
      }
    }

    function cancelGalleryRequest() {
      galleryRequest?.cancel();
      galleryRequest = null;
    }

    function commitGalleryMedia(index, revision, focusOrigin) {
      if (revision !== galleryRevision || signal.aborted || !root.isConnected) return;
      const previous = media[galleryIndex];
      const next = media[index];
      const restoreFocus = focusOrigin && document.activeElement === focusOrigin;
      galleryIndex = index;
      galleryIntentIndex = index;
      galleryRequest = null;
      setGalleryStatus();
      media.forEach(frame => {
        frame.getAnimations().forEach(animation => animation.cancel());
        frame.classList.toggle('is-active', frame === next);
        frame.inert = frame !== next;
        frame.setAttribute('aria-hidden', String(frame !== next));
        if (frame !== next) pauseFrame(frame);
        frame.hidden = frame !== next && frame !== previous;
      });
      next.hidden = false;
      if (previous && previous !== next) {
        next.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' }).finished.catch(() => {}).then(() => {
          if (revision === galleryRevision) media.forEach(frame => { frame.hidden = frame !== next; });
        });
      } else {
        media.forEach(frame => { frame.hidden = frame !== next; });
      }
      thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      const counter = $('[data-ca-gallery-count]');
      if (counter) counter.textContent = `${index + 1} / ${media.length}`;
      if (restoreFocus) thumbs[index]?.focus({ preventScroll: true });
      activateModel(next);
    }

    function selectMedia(index, focus = false) {
      if (!media.length) return;
      index = (index + media.length) % media.length;
      const revision = ++galleryRevision;
      cancelGalleryRequest();
      galleryIntentIndex = index;
      let focusOrigin = focus ? document.activeElement : null;
      if (focusOrigin?.dataset?.caGalleryRetry !== undefined && thumbs[galleryIndex]) {
        // Replacing the status removes its retry link; retain a stable focus target.
        thumbs[galleryIndex].focus({ preventScroll: true });
        focusOrigin = document.activeElement;
      }
      const next = media[index];
      const source = gallerySources.get(next);
      // A cancelled fade must settle on the last committed, usable picture.
      media.forEach((frame, i) => {
        frame.getAnimations().forEach(animation => animation.cancel());
        frame.hidden = i !== galleryIndex;
      });
      if (index === galleryIndex || next.dataset.mediaType !== 'image') {
        commitGalleryMedia(index, revision, focusOrigin);
        return;
      }
      setGalleryStatus(strings.loadingImage || 'Loading photograph…');
      const request = { cancel: () => {} };
      galleryRequest = request;
      const current = () => galleryRequest === request && revision === galleryRevision && !signal.aborted && root.isConnected;
      const ready = new Promise((resolve, reject) => {
        const image = source?.image;
        let settled = false;
        let decoding = false;
        let timer;
        const finish = (error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          image?.removeEventListener('load', decode);
          image?.removeEventListener('error', failed);
          signal.removeEventListener('abort', cancelled);
          if (error) reject(error); else resolve();
        };
        const failed = () => finish(new Error('Gallery photograph unavailable'));
        const cancelled = () => finish(new Error('Gallery request cancelled'));
        const decode = async () => {
          if (settled || decoding || !image?.complete || !image.naturalWidth) return;
          decoding = true;
          try {
            if (typeof image.decode === 'function') await image.decode();
            if (!current() || !image.naturalWidth || image.dataset.caFallbackUsed === 'true') { failed(); return; }
            finish();
          } catch { failed(); }
        };
        request.cancel = cancelled;
        if (!image) { failed(); return; }
        image.addEventListener('load', decode);
        image.addEventListener('error', failed);
        signal.addEventListener('abort', cancelled, { once: true });
        timer = setTimeout(failed, 20000);
        // Retry the requested photograph, never promote a different fallback as it.
        if (image.dataset.caFallbackUsed === 'true' || (image.complete && !image.naturalWidth)) {
          delete image.dataset.caFallbackUsed;
          ['sizes', 'srcset', 'src'].forEach(name => {
            if (source[name] !== null) image.setAttribute(name, source[name]);
            else image.removeAttribute(name);
          });
          image.alt = source.alt;
          const anchor = $('[data-ca-zoom]', next);
          if (anchor && source.href) anchor.setAttribute('href', source.href);
        }
        image.loading = 'eager';
        image.decoding = 'async';
        if (image.complete && image.naturalWidth) decode();
      });
      ready.then(() => {
        if (current()) commitGalleryMedia(index, revision, focusOrigin);
      }).catch(() => {
        if (!current()) return;
        galleryRequest = null;
        galleryIntentIndex = galleryIndex;
        setGalleryStatus(strings.galleryError || 'The next photograph could not load. The current view remains visible.', index);
      });
    }

    function setZoom(zoomed) {
      if (!lightbox) return;
      lightbox.classList.toggle('is-zoomed', zoomed);
      const button = $('[data-ca-lightbox-zoom]');
      button.setAttribute('aria-pressed', String(zoomed));
      button.textContent = `${zoomed ? strings.zoomOut || 'Zoom out' : strings.zoomIn || 'Zoom in'} ${zoomed ? '−' : '+'}`;
      const viewport = $('[data-ca-lightbox-viewport]');
      viewport.scrollTo(0, 0);
    }

    function cancelLightboxRequest() {
      lightboxRevision += 1;
      lightboxRequest?.loaders.forEach(loader => {
        loader.onload = null;
        loader.onerror = null;
        loader.removeAttribute('src');
      });
      lightboxRequest = null;
    }

    function showLightboxImage(index) {
      if (!photos.length) return;
      cancelLightboxRequest();
      lightboxIndex = (index + photos.length) % photos.length;
      const frame = photos[lightboxIndex];
      const anchor = $('[data-ca-zoom]', frame);
      const galleryImage = anchor.querySelector('img');
      const thumbnail = thumbs[Number(frame.dataset.mediaIndex)]?.querySelector('img');
      const image = $('[data-ca-lightbox-image]');
      const viewport = $('[data-ca-lightbox-viewport]');
      const counter = $('[data-ca-lightbox-count]');
      const count = `${lightboxIndex + 1} / ${photos.length}`;
      const request = { revision: lightboxRevision, loaders: [], fullReady: false };
      lightboxRequest = request;
      const current = () => lightboxRequest === request && request.revision === lightboxRevision && photos[lightboxIndex] === frame && !signal.aborted && root.isConnected;
      const loaded = candidate => candidate?.complete && candidate.naturalWidth > 0;
      const source = candidate => candidate?.currentSrc || candidate?.src || '';
      const display = url => {
        if (!current()) return;
        if (image.src !== url) image.src = url;
        image.alt = galleryImage?.alt || root.dataset.productTitle;
        image.dataset.mediaId = frame.dataset.mediaId;
        viewport.setAttribute('aria-busy', 'false');
        counter.textContent = count;
      };
      setZoom(false);
      counter.textContent = count;
      if (loaded(image) && image.src === anchor.href) {
        request.fullReady = true;
        display(anchor.href);
        return;
      }
      const preview = [galleryImage, thumbnail].find(loaded);
      if (preview) display(source(preview));
      else if (!(loaded(image) && image.dataset.mediaId === frame.dataset.mediaId)) {
        viewport.setAttribute('aria-busy', 'true');
        counter.textContent = `${count} · ${strings.loadingImage || 'Loading photograph…'}`;
      }
      const preload = (url, full) => {
        if (!url) return;
        const loader = new Image();
        loader.decoding = 'async';
        request.loaders.push(loader);
        loader.onload = async () => {
          try { await loader.decode(); } catch {}
          if (!current() || !loader.naturalWidth || (!full && request.fullReady)) return;
          if (full) request.fullReady = true;
          display(url);
        };
        loader.onerror = () => {
          if (!current() || !full) return;
          viewport.setAttribute('aria-busy', 'false');
          counter.textContent = count;
        };
        loader.src = url;
      };
      if (!preview) preload(source(thumbnail) || source(galleryImage), false);
      preload(anchor.href, true);
    }

    function openLightbox(index, opener) {
      const frame = media[index];
      const photoIndex = photos.indexOf(frame);
      if (photoIndex < 0 || typeof lightbox?.showModal !== 'function') return false;
      showLightboxImage(photoIndex);
      lightboxOpener = opener;
      lockedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      lightbox.showModal();
      $('[data-ca-lightbox-close]').focus({ preventScroll: true });
      return true;
    }

    async function updateVariant(select) {
      const selectedOption = select.selectedOptions[0];
      const targetURL = new URL(selectedOption?.dataset.productUrl || root.dataset.productUrl, location.origin);
      const values = $$('[data-ca-option-select]').map(option => option.value);
      targetURL.searchParams.set('option_values', values.join(','));
      const productPath = new URL(root.dataset.productUrl, location.origin).pathname;
      if (targetURL.pathname !== productPath) {
        location.assign(targetURL.toString());
        return;
      }
      variantRequest?.abort();
      variantRequest = new AbortController();
      const revision = ++variantRevision;
      const currentQuantity = Number($('[data-ca-quantity-input]')?.value) || 1;
      const currentPlan = $('[name="selling_plan"]')?.value;
      const position = select.dataset.optionPosition;
      updating = true;
      syncButtons();
      syncQuantity();
      setStatus(strings.updating);
      const renderURL = new URL(targetURL);
      renderURL.searchParams.set('section_id', root.dataset.sectionId);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) renderURL.searchParams.set('preview_theme_id', preview);
      try {
        const response = await fetch(renderURL, { signal: variantRequest.signal, headers: { Accept: 'text/html' }, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Variant request failed');
        const html = await response.text();
        if (revision !== variantRevision || signal.aborted) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const updated = documentFragment.querySelector('[data-ca-purchase]');
        if (!updated) throw new Error('Variant response is unavailable');
        purchase().replaceWith(document.importNode(updated, true));
        const quantity = $('[data-ca-quantity-input]');
        if (quantity) {
          const min = Number(quantity.min) || 1;
          const step = Number(quantity.step) || 1;
          const max = quantity.max ? Number(quantity.max) : Infinity;
          quantity.value = Math.min(max, min + Math.max(0, Math.floor((currentQuantity - min) / step)) * step);
        }
        const plan = $('[name="selling_plan"]');
        if (plan && [...plan.options].some(option => option.value === currentPlan)) plan.value = currentPlan;
        updating = false;
        enhancePurchase();
        const mediaId = purchase().dataset.variantMedia;
        const mediaIndex = media.findIndex(frame => frame.dataset.mediaId === mediaId);
        if (mediaIndex >= 0) selectMedia(mediaIndex);
        const url = new URL(location.href);
        if (purchase().dataset.variantId) {
          url.searchParams.set('variant', purchase().dataset.variantId);
          url.searchParams.delete('option_values');
        } else {
          url.searchParams.delete('variant');
          url.searchParams.set('option_values', values.join(','));
        }
        history.replaceState({}, '', url);
        $(`[data-ca-option-select][data-option-position="${position}"]`)?.focus({ preventScroll: true });
        queueScroll();
      } catch (error) {
        if (error.name === 'AbortError' || revision !== variantRevision || signal.aborted) return;
        $$('[data-ca-option-select]').forEach((option, i) => { option.value = committedOptions[i]; });
        setStatus(strings.variantError || 'This option could not be updated. Please try again.', true);
      } finally {
        if (revision === variantRevision) {
          updating = false;
          syncButtons();
          syncQuantity();
        }
      }
    }

    async function addToCart(event) {
      const form = event.target;
      if (event.defaultPrevented || !form.matches('.ca-pdp-product-form') || !event.submitter?.matches('[data-ca-add]')) return;
      if (!window.fetch || !window.FormData) return;
      event.preventDefault();
      if (adding || updating || !available()) return;
      if (!form.reportValidity()) return;
      const body = new FormData(form);
      body.delete('form_type');
      body.delete('utf8');
      [...body.keys()].filter(key => key.startsWith('options[')).forEach(key => body.delete(key));
      adding = true;
      setStatus(strings.adding);
      syncButtons();
      syncQuantity();
      const locked = [...form.querySelectorAll('select, input:not([type="hidden"])')].map(control => ({ control, disabled: control.disabled }));
      locked.forEach(({ control }) => { control.disabled = true; });
      let confirmed = false;
      try {
        const response = await fetch(`${root.dataset.cartAddUrl}.js`, { method: 'POST', body, signal, credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
        const item = await response.json();
        if (!response.ok || item.status) {
          const message = typeof item.description === 'string' ? item.description : typeof item.message === 'string' ? item.message : strings.error;
          const error = new Error(message);
          error.confirmed = true;
          throw error;
        }
        confirmed = true;
        let cart = null;
        try {
          const response = await fetch(root.dataset.cartJsonUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal });
          if (response.ok) cart = await response.json();
        } catch {}
        if (signal.aborted) return;
        setStatus(strings.added || 'Added to your bag.', false, true);
        document.dispatchEvent(new CustomEvent('ca:cart:updated', { detail: { cart, item, source: 'product' } }));
      } catch (error) {
        if (error.name === 'AbortError' || signal.aborted) return;
        setStatus(confirmed ? strings.added : error.confirmed ? error.message : strings.error, !confirmed, true);
      } finally {
        adding = false;
        locked.forEach(({ control, disabled }) => { if (control.isConnected) control.disabled = disabled; });
        syncButtons();
        syncQuantity();
      }
    }

    async function selectStory(index) {
      const revision = ++storyRevision;
      const frames = $$('[data-ca-story-photo]');
      const buttons = $$('[data-ca-story-step]');
      const next = frames[index];
      if (!next) return;
      buttons.forEach((button, i) => button.setAttribute('aria-busy', String(i === index)));
      const photograph = next.querySelector('img');
      if (photograph && !photograph.complete) {
        photograph.loading = 'eager';
        try { await photograph.decode(); } catch {}
      }
      if (revision !== storyRevision || signal.aborted) return;
      buttons.forEach(button => button.setAttribute('aria-busy', 'false'));
      if (photograph && !photograph.naturalWidth) return;
      frames.forEach((frame, i) => {
        frame.getAnimations().forEach(animation => animation.cancel());
        frame.hidden = i !== index;
        frame.classList.toggle('is-active', i === index);
        if (i === index) frame.animate([{ opacity: .2 }, { opacity: 1 }], { duration: 240, easing: 'ease-out' });
      });
      buttons.forEach((button, i) => {
        button.setAttribute('aria-pressed', String(i === index));
        button.classList.toggle('is-active', i === index);
      });
      const counter = $('[data-ca-study-index]');
      if (counter) counter.textContent = String(index + 1).padStart(2, '0');
    }

    function updateRelatedControls() {
      const rail = $('[data-ca-related-rail]');
      const controls = $('[data-ca-related-controls]');
      if (!rail || !controls) return;
      const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      controls.hidden = maximum < 2;
      $('[data-ca-related-dir="-1"]').disabled = rail.scrollLeft <= 2;
      $('[data-ca-related-dir="1"]').disabled = rail.scrollLeft >= maximum - 2;
    }

    function updateSpace() {
      const space = $('[data-ca-space]');
      if (!space) return;
      const unit = $('input[name^="units-"]:checked', space)?.value || 'm';
      const multiplier = unit === 'ft' ? .3048 : 1;
      const widthInput = $('[data-ca-room-width]', space);
      const depthInput = $('[data-ca-room-depth]', space);
      const roomWidth = Number(widthInput.value) * multiplier;
      const roomDepth = Number(depthInput.value) * multiplier;
      const result = $('[data-ca-space-result]', space);
      if (!widthInput.validity.valid || !depthInput.validity.valid || roomWidth <= 0 || roomDepth <= 0) {
        result.textContent = strings.invalidRoom;
        return;
      }
      const rotated = $('[data-ca-rotate]', space)?.getAttribute('aria-pressed') === 'true';
      const rawWidth = Number(space.dataset.widthMm) / 1000;
      const rawDepth = Number(space.dataset.depthMm) / 1000;
      const saunaWidth = rotated ? rawDepth : rawWidth;
      const saunaDepth = rotated ? rawWidth : rawDepth;
      const scale = Math.min(390 / Math.max(roomWidth, saunaWidth), 320 / Math.max(roomDepth, saunaDepth));
      const rw = roomWidth * scale;
      const rh = roomDepth * scale;
      const sw = saunaWidth * scale;
      const sh = saunaDepth * scale;
      const x = 285 - rw / 2;
      const y = 205 - rh / 2;
      const set = (element, attrs) => Object.entries(attrs).forEach(([key, value]) => element?.setAttribute(key, String(value)));
      set($('[data-ca-room-rect]', space), { x, y, width: rw, height: rh });
      set($('[data-ca-room-dim-x]', space), { d: `M${x} ${y + rh + 20}h${rw}M${x} ${y + rh + 14}v12M${x + rw} ${y + rh + 14}v12` });
      set($('[data-ca-room-dim-y]', space), { d: `M${x + rw + 20} ${y}v${rh}M${x + rw + 14} ${y}h12M${x + rw + 14} ${y + rh}h12` });
      const lx = $('[data-ca-room-label-x]', space);
      const ly = $('[data-ca-room-label-y]', space);
      set(lx, { x: 285, y: y + rh + 43 });
      set(ly, { x: x + rw + 44, y: 205, transform: `rotate(90 ${x + rw + 44} 205)` });
      lx.textContent = `${Number(widthInput.value).toFixed(2)} ${unit}`;
      ly.textContent = `${Number(depthInput.value).toFixed(2)} ${unit}`;
      const saunaAttrs = { x: 285 - sw / 2, y: 205 - sh / 2, width: sw, height: sh };
      set($('[data-ca-sauna-fill]', space), saunaAttrs);
      set($('[data-ca-sauna-hatch]', space), saunaAttrs);
      set($('[data-ca-sauna-front]', space), { d: `M${285 - sw / 2} ${205 + sh / 2}h${sw}` });
      set($('[data-ca-sauna-label]', space), { x: 285, y: 201 });
      set($('[data-ca-sauna-size]', space), { x: 285, y: 220 });
      const size = $('[data-ca-sauna-size]', space);
      if (size) size.textContent = `${(saunaWidth / multiplier).toFixed(2)} × ${(saunaDepth / multiplier).toFixed(2)} ${unit}`;
      const fits = roomWidth >= saunaWidth && roomDepth >= saunaDepth;
      $('.ca-pdp-plan', space)?.classList.toggle('is-small', !fits);
      result.textContent = rawWidth && rawDepth ? fits ? strings.roomLarger : strings.roomSmaller : strings.pendingDimensions;
      $$('[data-ca-unit]', space).forEach(label => { label.textContent = unit; });
      $('[data-ca-plan-units]', space).textContent = unit === 'ft' ? strings.feet : strings.metres;
    }

    function updateScroll() {
      scrollFrame = 0;
      if (!root.isConnected) return;
      const header = parseFloat(getComputedStyle(root).getPropertyValue('--ca-header-height')) || 94;
      const threshold = header + 90;
      if (root.dataset.caMotionReady !== 'true') {
        const chapters = $$('[data-ca-chapter]');
        let active = 'overview';
        chapters.forEach(chapter => { if (chapter.getBoundingClientRect().top <= threshold) active = chapter.dataset.caChapter; });
        $$('[data-ca-section-link]').forEach(link => {
          if (link.dataset.caSectionLink === active) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      }
      const bounds = root.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, (header - bounds.top) / Math.max(1, bounds.height - innerHeight)));
      const progressBar = $('.ca-pdp-reading-progress');
      if (progressBar && root.dataset.caMotionReady !== 'true') progressBar.style.transform = `scaleX(${progress})`;
      if (sticky) {
        const nextHidden = !(purchase()?.getBoundingClientRect().bottom < threshold && bounds.bottom > 180);
        if (sticky.hidden !== nextHidden) {
          sticky.hidden = nextHidden;
          renderPurchaseStatus();
        }
      }
      const stage = $('[data-ca-story-stage]');
      if (stage && root.dataset.caMotionReady !== 'true') {
        const rect = stage.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < innerHeight) {
          const travel = Math.max(-1, Math.min(1, (innerHeight / 2 - rect.top - rect.height / 2) / innerHeight));
          const amount = innerWidth < 750 ? 7 : Math.min(18, rect.height * .02);
          stage.style.setProperty('--ca-photo-drift', `${travel * amount}px`);
          stage.style.setProperty('--ca-inset-drift', `${travel * -amount}px`);
        }
      }
    }

    function queueScroll() { if (!scrollFrame) scrollFrame = requestAnimationFrame(updateScroll); }

    on(root, 'submit', addToCart);
    on(root, 'change', event => {
      if (event.target.matches('[data-ca-option-select]')) updateVariant(event.target);
      if (event.target.matches('[data-ca-quantity-input]')) syncQuantity();
      if (event.target.matches('input[name^="units-"]')) {
        const toFeet = event.target.value === 'ft';
        $$('[data-ca-room-width], [data-ca-room-depth]').forEach(input => { input.value = (Number(input.value) * (toFeet ? 1 / .3048 : .3048)).toFixed(2); });
        updateSpace();
      }
    });
    on(root, 'input', event => {
      if (event.target.matches('[data-ca-room-width], [data-ca-room-depth]')) updateSpace();
      if (event.target.matches('[data-ca-quantity-input]')) syncNativeWallets();
    });
    on(root, 'click', event => {
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.tagName === 'A' && (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0)) return;
      if (target.matches('[data-ca-thumbnail]')) { event.preventDefault(); selectMedia(Number(target.dataset.mediaIndex)); }
      if (target.matches('[data-ca-gallery-retry]')) { event.preventDefault(); selectMedia(Number(target.dataset.caGalleryRetry), true); }
      if (target.matches('[data-ca-gallery-prev]')) selectMedia(galleryIntentIndex - 1);
      if (target.matches('[data-ca-gallery-next]')) selectMedia(galleryIntentIndex + 1);
      if (target.matches('[data-ca-zoom]')) {
        if (Date.now() < suppressClickUntil) { event.preventDefault(); return; }
        if (openLightbox(Number(target.dataset.mediaIndex), target)) event.preventDefault();
      }
      if (target.matches('[data-ca-study-zoom]')) {
        const index = media.findIndex(frame => frame.dataset.mediaId === target.dataset.mediaId);
        if (index >= 0 && openLightbox(index, target)) event.preventDefault();
      }
      if (target.matches('[data-ca-lightbox-close]')) lightbox.close();
      if (target.matches('[data-ca-lightbox-prev]')) showLightboxImage(lightboxIndex - 1);
      if (target.matches('[data-ca-lightbox-next]')) showLightboxImage(lightboxIndex + 1);
      if (target.matches('[data-ca-lightbox-zoom]')) setZoom(!lightbox.classList.contains('is-zoomed'));
      if (target.matches('[data-ca-quantity]')) {
        const input = $('[data-ca-quantity-input]');
        const min = Number(input.min) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        const step = Number(input.step) || 1;
        const previous = input.value;
        input.value = Math.max(min, Math.min(max, (Number(input.value) || min) + Number(target.dataset.caQuantity) * step));
        if (input.value !== previous) input.dispatchEvent(new Event('change', { bubbles: true }));
        syncQuantity();
      }
      if (target.matches('[data-ca-sticky-add]')) {
        const form = productForm();
        const addButton = form?.querySelector('[data-ca-add]');
        if (addButton && !addButton.disabled && form.reportValidity()) form.requestSubmit(addButton);
      }
      if (target.matches('[data-ca-story-step]')) selectStory(Number(target.dataset.caStoryStep));
      if (target.matches('[data-ca-related-dir]')) {
        const rail = $('[data-ca-related-rail]');
        if (rail) rail.scrollBy({ left: Number(target.dataset.caRelatedDir) * rail.clientWidth * .9, behavior: 'smooth' });
      }
      if (target.matches('[data-ca-rotate]')) { target.setAttribute('aria-pressed', String(target.getAttribute('aria-pressed') !== 'true')); updateSpace(); }
    });
    on(gallery, 'keydown', event => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      const thumb = event.target.closest('[data-ca-thumbnail]');
      if (thumb && ['ArrowLeft', 'ArrowRight', 'Home', 'End', ' '].includes(event.key)) {
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? media.length - 1 : event.key === ' ' ? Number(thumb.dataset.mediaIndex) : galleryIntentIndex + (event.key === 'ArrowRight' ? 1 : -1);
        selectMedia(index, true);
      }
    });
    on(lightbox, 'keydown', event => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); showLightboxImage(lightboxIndex - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); showLightboxImage(lightboxIndex + 1); }
    });
    on(lightbox, 'close', () => {
      if (lightbox.open) return;
      cancelLightboxRequest();
      document.documentElement.style.overflow = lockedOverflow || '';
      setZoom(false);
      lightboxOpener?.focus({ preventScroll: true });
    });
    on($('[data-ca-lightbox-viewport]'), 'click', () => setZoom(!lightbox.classList.contains('is-zoomed')));
    on($('[data-ca-gallery-stage]'), 'pointerdown', event => {
      if (event.pointerType === 'touch' && media[galleryIndex]?.dataset.mediaType === 'image') touchStart = { x: event.clientX, y: event.clientY };
    }, { passive: true });
    on($('[data-ca-gallery-stage]'), 'pointerup', event => {
      if (!touchStart) return;
      const dx = event.clientX - touchStart.x;
      const dy = event.clientY - touchStart.y;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        suppressClickUntil = Date.now() + 350;
        selectMedia(galleryIntentIndex + (dx < 0 ? 1 : -1));
      }
      touchStart = null;
    }, { passive: true });
    on($('[data-ca-gallery-stage]'), 'pointercancel', () => { touchStart = null; }, { passive: true });
    on(window, 'scroll', queueScroll, { passive: true });
    on(window, 'resize', queueScroll, { passive: true });
    on(window, 'resize', updateRelatedControls, { passive: true });
    on($('[data-ca-related-rail]'), 'scroll', updateRelatedControls, { passive: true });
    on(document, 'shopify:block:select', event => {
      if (!root.contains(event.target)) return;
      const step = event.target.closest('[data-ca-story-step]');
      if (step) selectStory(Number(step.dataset.caStoryStep));
    });

    let revealObserver;
    if ('IntersectionObserver' in window && root.dataset.caMotionReady !== 'true') {
      revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.animate([{ opacity: .75, transform: 'translateY(12px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 480, easing: 'cubic-bezier(.23,1,.32,1)' });
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: .12 });
      $$('[data-ca-reveal]').forEach(element => revealObserver.observe(element));
    }

    $$('[data-ca-gallery-controls], [data-ca-space-interactive]').forEach(element => { element.hidden = false; });
    thumbs.forEach(thumb => thumb.setAttribute('role', 'button'));
    $$('[data-ca-story-step]').forEach(button => { button.disabled = false; });
    enhancePurchase();
    selectMedia(galleryIndex);
    updateSpace();
    updateRelatedControls();
    queueScroll();
    root.dataset.caReady = 'true';
    const destroy = () => {
      galleryRevision += 1;
      cancelGalleryRequest();
      galleryStatus?.remove();
      stickyStatus?.remove();
      purchaseAnnouncement?.remove();
      cancelLightboxRequest();
      if (lightbox?.open) {
        lightbox.close();
        document.documentElement.style.overflow = lockedOverflow || '';
      }
      life.abort();
      variantRequest?.abort();
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      revealObserver?.disconnect();
      modelUIs.forEach(ui => ui.destroy?.());
      instances.delete(root);
    };
    instances.set(root, { destroy });
  }

  function init(context = document) {
    if (context.matches?.('[data-ca-product]')) mount(context);
    context.querySelectorAll('[data-ca-product]').forEach(mount);
  }
  window.CedarAmberProductPage = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => { if (event.target.contains(root)) instance.destroy(); });
  });
})();
